import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Calendar } from '@/components/ui/Calendar';
import { Input } from '@/components/ui/Input';
import {
  addPastedFsns,
  duplicateCampaignName,
  removeFsn,
} from '@/components/flipkart/flipkartCampaignDuplicate';
import { isoToLocalDate, localDateToIso } from '@/utils/dateRangePicker';

export type FlipkartCampaignDuplicateInput = {
  name: string;
  startDate: string;
  endDate: string | null;
  productIds: string[];
  makeLive: boolean;
};

export function FlipkartCampaignDuplicateModal({
  sourceName,
  startDate = null,
  endDate = null,
  fsns,
  productNames,
  pending,
  error = null,
  onCancel,
  onConfirm,
}: {
  sourceName: string | null;
  startDate?: string | null;
  endDate?: string | null;
  fsns: string[];
  productNames: ReadonlyMap<string, string>;
  pending: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (input: FlipkartCampaignDuplicateInput) => void;
}) {
  const [name, setName] = useState(() => duplicateCampaignName(sourceName));
  const [start, setStart] = useState<Date | undefined>(() => isoToLocalDate(startDate ?? ''));
  const [end, setEnd] = useState<Date | undefined>(() => isoToLocalDate(endDate ?? ''));
  const [fsnIds, setFsnIds] = useState(fsns);
  const [paste, setPaste] = useState('');
  const [makeLive, setMakeLive] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const shownError = error ?? localError;
  const startIso = start ? localDateToIso(start) : '';
  const endIso = end ? localDateToIso(end) : '';
  const endBeforeStart = Boolean(startIso && endIso && endIso < startIso);
  const canSubmit = Boolean(name.trim() && startIso && fsnIds.length > 0 && !endBeforeStart);

  function submit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setLocalError('Campaign name is required');
      return;
    }
    if (!startIso) {
      setLocalError('Start date is required');
      return;
    }
    if (endBeforeStart) {
      setLocalError('End date must be on or after the start date');
      return;
    }
    if (fsnIds.length === 0) {
      setLocalError('At least one FSN is required');
      return;
    }
    setLocalError(null);
    onConfirm({
      name: trimmed,
      startDate: startIso,
      endDate: endIso || null,
      productIds: fsnIds,
      makeLive,
    });
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-surface shadow-xl">
        <div className="overflow-y-auto p-5">
          <button
            type="button"
            className="absolute right-4 top-4 rounded-md p-1 text-foreground-muted hover:bg-surface-muted hover:text-foreground"
            onClick={onCancel}
            disabled={pending}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <h3 className="pr-8 text-base font-semibold text-foreground">Duplicate campaign</h3>
          <p className="mt-2 text-body-sm text-foreground-muted">
            Keyword bid amounts stay on the original campaign. The copy keeps the keyword list and
            placement bids.
          </p>
          <label className="mt-4 block text-body-sm font-medium text-foreground" htmlFor="duplicate-campaign-name">
            Campaign name
          </label>
          <Input
            id="duplicate-campaign-name"
            className="mt-1"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={pending}
          />
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-2 text-body-sm font-medium text-foreground">Start date</div>
              <Calendar
                mode="single"
                selected={start}
                onSelect={setStart}
                defaultMonth={start}
                aria-label="Start date"
              />
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-body-sm font-medium text-foreground">End date</span>
                <Button
                  type="button"
                  variant="secondary"
                  className="h-7 px-2 text-body-sm"
                  onClick={() => setEnd(undefined)}
                  disabled={pending || !end}
                >
                  Clear
                </Button>
              </div>
              <Calendar
                mode="single"
                selected={end}
                onSelect={setEnd}
                defaultMonth={end ?? start}
                aria-label="End date"
              />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-body-sm font-medium text-foreground">FSNs</div>
            <ul className="mt-2 max-h-40 divide-y divide-border overflow-y-auto rounded-lg border border-border">
              {fsnIds.length === 0 ? (
                <li className="px-3 py-2 text-body-sm text-foreground-muted">No FSNs yet. Paste at least one.</li>
              ) : (
                fsnIds.map((id) => {
                  const productName = productNames.get(id);
                  return (
                    <li key={id} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span className="min-w-0">
                        <span className="block truncate text-body-sm text-foreground">{id}</span>
                        {productName ? (
                          <span className="block truncate text-body-sm text-foreground-muted">{productName}</span>
                        ) : null}
                      </span>
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-7 shrink-0 px-2 text-body-sm"
                        onClick={() => setFsnIds((current) => removeFsn(current, id))}
                        disabled={pending}
                        aria-label={`Remove ${id}`}
                      >
                        Remove
                      </Button>
                    </li>
                  );
                })
              )}
            </ul>
            <label className="mt-3 block text-body-sm text-foreground-muted" htmlFor="duplicate-fsn-paste">
              Add FSNs
            </label>
            <textarea
              id="duplicate-fsn-paste"
              className="mt-1 min-h-16 w-full rounded-lg border border-border bg-surface px-3 py-2 text-body-md text-foreground shadow-sm placeholder:text-foreground-subtle focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              value={paste}
              onChange={(event) => setPaste(event.target.value)}
              placeholder="Paste FSN ids"
              disabled={pending}
            />
            <Button
              type="button"
              variant="secondary"
              className="mt-2"
              onClick={() => {
                setFsnIds((current) => addPastedFsns(current, paste));
                setPaste('');
              }}
              disabled={pending || !paste.trim()}
            >
              Add FSNs
            </Button>
          </div>
          <label className="mt-4 flex items-start gap-2 text-body-sm text-foreground">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={makeLive}
              onChange={(event) => setMakeLive(event.target.checked)}
              disabled={pending}
            />
            <span>
              Make campaign live
              <span className="mt-0.5 block text-foreground-muted">
                Leave this off and the copy stays a draft.
              </span>
            </span>
          </label>
          {shownError ? <p className="mt-3 text-body-sm text-error">{shownError}</p> : null}
          {endBeforeStart ? (
            <p className="mt-3 text-body-sm text-error">End date must be on or after the start date.</p>
          ) : null}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border p-4">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={pending || !canSubmit}>
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Duplicate'}
          </Button>
        </div>
      </div>
    </div>
  );
}
